import { useState, useEffect } from "react";
import {
  Check, X, Minus, Sparkles, BadgeCheck, Loader2, Send, CalendarClock, Gauge,
  ShieldCheck, Users, Briefcase, Clock, Truck, Crown, FileSignature, Zap, Wrench,
} from "lucide-react";

// Srovnání EffectDeal vs. klasická autopůjčovna vs. operativní leasing.
// Diferenciace bez ceny: flexibilita, délka závazku, km limity, sankce.
const COLS = ["EffectDeal", "Klasická půjčovna", "Operativní leasing"];

const ROWS = [
  { label: "Minimální doba pronájmu", icon: CalendarClock, values: [{ t: "už od 1 měsíce", s: "good" }, { t: "dny / týdny", s: "mid" }, { t: "24–60 měsíců", s: "bad" }] },
  { label: "Smluvní vázání / závazek", icon: FileSignature, values: [{ t: "krátké, flexibilní", s: "good" }, { t: "krátké", s: "good" }, { t: "roky, vázán na celou dobu", s: "bad" }] },
  { label: "Limit najetých km", icon: Gauge, values: [{ t: "férové, řešíme dohodou", s: "good" }, { t: "přísný denní limit", s: "bad" }, { t: "roční limit + pokuty za přejetí", s: "bad" }] },
  { label: "Schválení a vyřízení", icon: Zap, values: [{ t: "ihned, bez tisíce podmínek", s: "good" }, { t: "ihned", s: "good" }, { t: "scoring, dny–týdny", s: "bad" }] },
  { label: "Pojištění (hav. + povinné ručení)", icon: ShieldCheck, values: [{ s: "yes" }, { s: "partial" }, { s: "yes" }] },
  { label: "Servis, údržba a dálniční známka", icon: Wrench, values: [{ t: "vše v ceně", s: "good" }, { s: "partial" }, { t: "vázáno smlouvou", s: "mid" }] },
];

const Cell = ({ v, highlight }) => {
  const good = v.s === "yes" || v.s === "good";
  const bad = v.s === "no" || v.s === "bad";
  const icon = good
    ? <Check className={`w-3.5 h-3.5 shrink-0 ${highlight ? "text-orange-400" : "text-emerald-400"}`} />
    : bad
      ? <X className="w-3.5 h-3.5 shrink-0 text-red-400/80" />
      : <Minus className="w-3.5 h-3.5 shrink-0 text-zinc-500" />;
  const tint = highlight
    ? "bg-orange-500/15 ring-1 ring-orange-500/30"
    : good ? "bg-emerald-500/10" : bad ? "bg-red-500/[0.07]" : "bg-white/[0.04]";
  return (
    <div className={`inline-flex items-center gap-1.5 text-sm rounded-full px-2.5 py-1 ${tint}`}>
      {icon}
      <span className={`${v.t ? (highlight ? "text-white font-medium" : "text-zinc-300") : "sr-only"}`}>{v.t || v.s}</span>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SCENARIOS = [
  { key: "osvc", icon: Briefcase, label: "OSVČ / podnikatel", title: "Auto hned a bez papírování", text: "Vůz dostanete rychle, náklad si dáte do účetnictví a nejste vázáni na roky jako u leasingu – ani neprocházíte zdlouhavým prověřováním bonity." },
  { key: "firma", icon: Users, label: "Firma / flotila", title: "Flotila bez vázání kapitálu", text: "Pořiďte více vozů bez velké počáteční investice, flexibilně je obměňujte a mějte vše v jedné měsíční ceně – jednoduché pro účtárnu." },
  { key: "nove", icon: Truck, label: "Čekáte na nové auto", title: "Přemostění, než dorazí vaše auto", text: "Objednané auto má dlouhou dodací lhůtu? Pronajměte si u nás vůz klidně jen na pár měsíců a nezůstaňte bez mobility." },
  { key: "sezona", icon: Clock, label: "Sezónní / dočasná potřeba", title: "Auto přesně na dobu, kdy ho potřebujete", text: "Zakázka, stěhování, dočasný projekt? Vůz vrátíte, až ho přestanete potřebovat – žádný závazek na roky jako u operáku." },
];

const ComparisonSection = () => {
  const [profile, setProfile] = useState("osvc");
  const [lead, setLead] = useState({ email: "", phone: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/stats/calculator`).then((r) => r.json()).then(setStats).catch(() => {});
  }, []);
  const socialCount = stats ? stats.total : 0;
  const socialLabel = "celkem";
  const active = SCENARIOS.find((s) => s.key === profile) || SCENARIOS[0];

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.email && !lead.phone) { setErr("Zadejte e-mail nebo telefon."); return; }
    setSending(true); setErr(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "", email: lead.email, phone: lead.phone, vehicleType: "", duration: active.label,
          message: `Poptávka z homepage – profil zájemce: ${active.label}. Zájem o flexibilní dlouhodobý pronájem.`,
        }),
      });
      if (!res.ok) throw new Error("Odeslání se nezdařilo, zkuste to prosím znovu.");
      setSent(true);
    } catch (e2) { setErr(e2.message); } finally { setSending(false); }
  };

  return (
    <section id="comparison" className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0a0a0a]" data-testid="comparison-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 sm:mb-14 reveal">
          <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-3">
            <Sparkles className="w-4 h-4" /> <span>Proč to dává smysl</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">Levnější než půjčovna,<br className="hidden sm:block" /> flexibilnější než operák</h2>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto text-sm sm:text-base">
            Spojujeme to nejlepší z obou světů – svobodu krátkodobého pronájmu a výhodnost dlouhodobého leasingu. Krátké závazky, férové km a vše v ceně.
          </p>
          <div className="inline-flex items-center gap-2 mt-5 bg-orange-500/15 border border-orange-500/30 text-orange-200 text-sm font-semibold px-4 py-2 rounded-full" data-testid="comparison-unique-badge">
            <BadgeCheck className="w-4 h-4" /> Unikát na českém trhu – tuhle kombinaci jinde nenajdete
          </div>
        </div>

        {/* Desktop / tablet table */}
        <div className="hidden md:block reveal reveal-delay-1 pt-5">
          {/* Header */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-x-3">
            <div />
            {COLS.map((c, i) => (
              i === 0 ? (
                <div key={c} className="relative bg-gradient-to-b from-orange-500/30 to-orange-500/10 border-x border-t border-orange-500/40 rounded-t-2xl p-4 pt-5 text-center shadow-[0_0_50px_-10px_rgba(249,115,22,0.55)]" data-testid="comparison-col-0">
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-500 text-black text-[11px] font-extrabold tracking-wide px-3 py-1 rounded-full whitespace-nowrap shadow-lg" data-testid="comparison-winner-badge">
                    <Crown className="w-3 h-3" /> NEJLEPŠÍ VOLBA
                  </div>
                  <span className="font-['Cabinet_Grotesk'] font-bold text-orange-200 text-lg">EffectDeal</span>
                </div>
              ) : (
                <div key={c} className="p-4 text-center font-['Cabinet_Grotesk'] font-bold text-zinc-300 self-end" data-testid={`comparison-col-${i}`}>
                  {c}
                </div>
              )
            ))}
          </div>
          {/* Rows */}
          <div>
            {ROWS.map((row, idx) => {
              const last = idx === ROWS.length - 1;
              return (
                <div key={row.label} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-x-3 reveal items-stretch group" style={{ transitionDelay: `${idx * 55}ms` }}>
                  <div className="py-3.5 pr-2 flex items-center gap-2.5 text-sm text-zinc-300 border-b border-white/5 transition-colors group-hover:text-white">
                    <row.icon className="w-4 h-4 text-zinc-500 shrink-0 transition-colors group-hover:text-orange-400/80" />
                    <span>{row.label}</span>
                  </div>
                  {/* EffectDeal highlighted column */}
                  <div className={`relative bg-orange-500/[0.08] border-x border-orange-500/40 ${last ? "rounded-b-2xl border-b" : ""} py-3.5 flex items-center justify-center transition-colors group-hover:bg-orange-500/[0.13]`}>
                    <Cell v={row.values[0]} highlight />
                  </div>
                  {row.values.slice(1).map((v, i) => (
                    <div key={i} className="py-3.5 flex items-center justify-center border-b border-white/5">
                      <Cell v={v} />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: per-metric comparison (EffectDeal vs Klasická půjčovna vs Operativní leasing) */}
        <div className="md:hidden space-y-3 reveal reveal-delay-1">
          {ROWS.map((row, idx) => (
            <div key={row.label} className="glass rounded-2xl p-4 border border-white/10 reveal" style={{ transitionDelay: `${idx * 45}ms` }} data-testid="comparison-mobile-row">
              <div className="flex items-center gap-2 mb-3 text-sm text-zinc-200 font-medium">
                <row.icon className="w-4 h-4 text-orange-400/80 shrink-0" />
                <span>{row.label}</span>
              </div>
              <div className="space-y-1.5">
                {COLS.map((col, i) => (
                  <div key={col} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 ${i === 0 ? "bg-orange-500/[0.1] ring-1 ring-orange-500/30" : "bg-white/[0.03]"}`}>
                    <span className={`text-[11px] inline-flex items-center gap-1 shrink-0 ${i === 0 ? "text-orange-300 font-semibold" : "text-zinc-400"}`}>
                      {i === 0 && <Crown className="w-3 h-3" />}{col}
                    </span>
                    <span className="text-right"><Cell v={row.values[i]} highlight={i === 0} /></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-zinc-500 text-center px-4 pt-1">Oproti klasické půjčovně jste na dlouhodobém nájmu výhodnější, oproti operativnímu leasingu nejste vázáni roky ani drakonickými podmínkami.</p>
        </div>

        {/* Pro koho je EffectDeal ideální + lead magnet */}
        <div className="glass rounded-2xl p-6 sm:p-8 mt-8 reveal reveal-delay-2 border border-orange-500/20" data-testid="usecase-tool">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-orange-400" />
            <h3 className="font-['Cabinet_Grotesk'] text-lg sm:text-xl font-bold">Pro koho je EffectDeal ideální?</h3>
          </div>
          <p className="text-zinc-400 text-sm mb-5">Vyberte, co vás vystihuje – ukážeme vám, proč je u nás pronájem výhodnější než operák i klasická půjčovna.</p>

          <div className="flex flex-wrap gap-2 mb-5">
            {SCENARIOS.map((s) => (
              <button key={s.key} onClick={() => setProfile(s.key)} className={`text-sm px-4 py-2 rounded-full border inline-flex items-center gap-2 transition-colors ${profile === s.key ? "border-orange-500 bg-orange-500/15 text-orange-300" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`usecase-${s.key}`}>
                <s.icon className="w-4 h-4" /> {s.label}
              </button>
            ))}
          </div>

          <div className="rounded-xl bg-orange-500/[0.07] border border-orange-500/20 p-5 mb-5" data-testid="usecase-result">
            <h4 className="font-['Cabinet_Grotesk'] font-bold text-orange-200 mb-1.5 flex items-center gap-2"><active.icon className="w-4 h-4" /> {active.title}</h4>
            <p className="text-sm text-zinc-300 leading-relaxed">{active.text}</p>
          </div>

          {/* Lead magnet */}
          <div className="mt-6 pt-6 border-t border-white/10" data-testid="savings-lead">
            {socialCount > 0 && (
              <p className="text-xs text-orange-300/90 mb-3 inline-flex items-center gap-1.5" data-testid="savings-social-proof">
                <BadgeCheck className="w-3.5 h-3.5" /> Nabídku si {socialLabel} vyžádalo už {socialCount}× zájemců.
              </p>
            )}
            {sent ? (
              <p className="text-emerald-400 text-sm font-medium inline-flex items-center gap-2" data-testid="savings-lead-success">
                <Check className="w-4 h-4" /> Děkujeme! Ozveme se vám obratem s nabídkou na míru.
              </p>
            ) : (
              <>
                <p className="text-sm text-zinc-200 mb-3 font-medium">Chcete nabídku na míru? Pošleme vám ji obratem – nezávazně.</p>
                <form onSubmit={submitLead} className="flex flex-col sm:flex-row gap-2">
                  <input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Váš e-mail" className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500" data-testid="savings-lead-email" />
                  <input type="tel" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Telefon" className="sm:w-44 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500" data-testid="savings-lead-phone" />
                  <button type="submit" disabled={sending} className="btn-accent inline-flex items-center justify-center gap-2 shrink-0 disabled:opacity-60" data-testid="savings-lead-submit">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Chci nabídku
                  </button>
                </form>
                {err && <p className="text-red-400 text-xs mt-2" data-testid="savings-lead-error">{err}</p>}
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-10 reveal reveal-delay-2">
          <a href="#contact" className="btn-accent inline-flex" data-testid="comparison-cta">Chci nezávaznou nabídku</a>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
